import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Badge } from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { getLoginAttemptsByUser } from 'Settings/Security/SecFunctions';
import LoginAttemptsModal from 'Dialogs/LoginAttemptsModal';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  button: {
    textTransform: 'none',
    fontSize: '0.75rem',
    padding: '4px 10px',
    minWidth: 'auto',
    whiteSpace: 'nowrap',
  },
  redButton: {
    backgroundColor: theme.palette.error.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  normalButton: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: theme.palette.grey[400],
    },
  },
  badge: {
    '& .MuiBadge-badge': {
      right: -3,
      top: 10,
    },
  },
  icon: {
    fontSize: '1rem',
    marginRight: theme.spacing(0.5),
  },
}));

const LoginAttemptsButton = props => {
  const classes = useStyles();
  const { ptsUserID, username, disabled, onViewComplete } = props;
  const [showModal, setShowModal] = useState(false);
  const [unviewedCount, setUnviewedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);

  useEffect(() => {
    if (ptsUserID && !dataFetched) {
      fetchUnviewedCount();
    }
  }, [ptsUserID]);

  const fetchUnviewedCount = async () => {
    try {
      setLoading(true);
      const result = await getLoginAttemptsByUser(ptsUserID);
      const unviewed = result.data.filter(attempt => !attempt.IsViewed).length;
      setUnviewedCount(unviewed);
      setTotalCount(result.data.length);
      setDataFetched(true);
    } catch (error) {
      console.error('Error fetching unviewed count:', error);
      setDataFetched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleViewComplete = async () => {
    // Refresh count after viewing
    setDataFetched(false);
    await fetchUnviewedCount();
    if (onViewComplete) {
      onViewComplete();
    }
  };

  const hasUnviewed = unviewedCount > 0;

  // Don't render anything while loading initially or if no records exist
  if (loading || totalCount === 0) {
    return null;
  }

  return (
    <div className={classes.container}>
      <Badge
        badgeContent={unviewedCount}
        color="error"
        className={classes.badge}
        invisible={!hasUnviewed}>
        <Button
          variant="contained"
          size="small"
          onClick={handleClick}
          disabled={disabled || !ptsUserID || loading}
          className={`${classes.button} ${hasUnviewed ? classes.redButton : classes.normalButton}`}>
          <VisibilityIcon className={classes.icon} />
          Login Attempts
        </Button>
      </Badge>

      {showModal && (
        <LoginAttemptsModal
          ptsUserID={ptsUserID}
          username={username}
          onClose={handleClose}
          onViewComplete={handleViewComplete}
        />
      )}
    </div>
  );
};

export default LoginAttemptsButton;
